import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Sparkles, Handshake, Globe2 } from "lucide-react";
import { Eyebrow, SectionHeading, FadeUp } from "../components/site/Primitives";
import SceneImage from "../components/site/SceneImage";

export default function AboutPage() {
  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-24 lg:pb-32 kraft-texture">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>About BlacPac</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              We are building Africa&rsquo;s leading{" "}
              <span className="text-[#E85D2F]">compostable</span>{" "}
              food packaging company.
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-10 text-lg md:text-xl text-[#F7F5F0]/75 leading-relaxed font-light max-w-3xl">
              BlacPac is a BlacVerse Group company focused on providing
              high-quality, affordable and sustainable food packaging solutions
              across Africa — starting in Ghana and scaling outward.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="mt-6 font-grotesk text-sm tracking-[0.3em] uppercase text-[#E85D2F]/90 font-semibold">
              Made for African food · Made for the future
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Editorial story */}
      <section className="py-24 lg:py-32 bg-[#121211] border-y border-[#F7F5F0]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <FadeUp className="lg:col-span-5">
            <div className="sticky top-32 rounded-3xl overflow-hidden aspect-[4/5]">
              <SceneImage
                scene="kitchen-plating"
                className="h-full w-full object-cover"
                alt="A chef plating jollof rice into a BlacPac compostable bowl in an Accra restaurant kitchen."
                sizes="(min-width: 1024px) 40vw, 100vw"
              />
            </div>
          </FadeUp>

          <div className="lg:col-span-7 space-y-12">
            <FadeUp>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">Why Ghana</p>
              <h2 className="mt-4 font-grotesk text-3xl md:text-4xl font-bold tracking-tight text-[#F7F5F0]">
                Strategic gateway to West Africa.
              </h2>
              <p className="mt-5 text-[#F7F5F0]/70 leading-relaxed font-light">
                Ghana is a stable democracy with a large and growing food-service
                market, a confirmed government ban on Styrofoam from January
                2027, and strong demand for sustainable solutions. It is the
                natural launchpad for a continent-scale packaging company.
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">Why now</p>
              <h2 className="mt-4 font-grotesk text-3xl md:text-4xl font-bold tracking-tight text-[#F7F5F0]">
                Businesses are preparing for the transition. Early movers will lead.
              </h2>
              <p className="mt-5 text-[#F7F5F0]/70 leading-relaxed font-light">
                The time to build supply capacity is now — not the week before
                the deadline. We are securing container-load volumes, finishing
                capacity and distribution partnerships across Ghana so our
                clients are never caught short.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">The ecosystem</p>
              <h2 className="mt-4 font-grotesk text-3xl md:text-4xl font-bold tracking-tight text-[#F7F5F0]">
                Powered by BlacVerse Group.
              </h2>
              <p className="mt-5 text-[#F7F5F0]/70 leading-relaxed font-light">
                BlacPac is one of several modern brands inside the BlacVerse
                Group, sharing visual identity, operational discipline and a
                serious commitment to building category-leading African
                businesses with global reach.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading eyebrow="What we stand for" title="Four operating principles." />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Building2, title: "Designed for performance", body: "We design for real kitchens — speed, stack-ability, structural integrity and brand presentation." },
              { icon: Sparkles, title: "Built for every food business", body: "From single jollof spots to national restaurant chains. From hotel canteens to street-food vendors." },
              { icon: Handshake, title: "Long-term supply partner", body: "Monthly recurring orders, container-load imports and predictable stockholding." },
              { icon: Globe2, title: "Made for African food", body: "Sized, shaped and finished for the dishes that actually fill takeaway counters across the continent." },
            ].map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-7 hover:border-[#E85D2F]/40 transition-colors">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E85D2F] text-[#0A0A09]">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-grotesk text-xl font-bold text-[#F7F5F0] tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#F7F5F0]/65 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="rounded-[2rem] bg-[#F2EEE3] text-[#0A0A09] p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-grotesk text-3xl md:text-5xl font-black tracking-tighter leading-tight">
                Let&rsquo;s build the future together.
              </h3>
              <p className="mt-4 text-[#0A0A09]/70 max-w-xl font-light">
                Tell us about your menu, your volume and your brand — we&rsquo;ll come back with a practical recommendation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold hover:bg-[#F47A4A] transition-colors"
              >
                Start the conversation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
